import React, {
  DetailedHTMLProps,
  InputHTMLAttributes,
  ReactElement,
  TextareaHTMLAttributes,
} from "react";
import { ComponentWrapperHOC } from "../../components/WrapperHoc";
import s from "./input.module.scss";
import { ButtonSize } from "../../sharedTypes/components";
import ErrorInfoIcon from "../../assets/icons/error-info-icon.svg";
import qtip from "../../assets/icons/q-tip.svg";
interface Props
  extends DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  success?: boolean;
  error?: boolean;
  errorText?: string;
  sideLabel?: string;
  label?: string;
  labelStyle?: React.CSSProperties;
  textStyle?: React.CSSProperties;
  containerStyle?: React.CSSProperties;
  fullwidth?: boolean;
  iconRight?: ReactElement;
  revealSecret?: ReactElement;
  sizing?: ButtonSize;
  tip?: string;
}

export function InputField(props: Props) {
  const {
    error,
    errorText,
    sideLabel,
    textStyle,
    containerStyle,
    label,
    labelStyle,
    required,
    fullwidth,
    width = "auto",
    iconRight,
    revealSecret,
    sizing,
    tip,
    ...rest
  } = props;

  const inputSideBorderRadius = sideLabel ? "0px" : undefined;
  const wrapperWidth = fullwidth ? "100%" : width;

  return (
    <ComponentWrapperHOC
      wrap={!!label}
      wrapper={(children) => (
        <label
          style={{ width: wrapperWidth, ...labelStyle }}
          className={s.label}
        >
          <span className="tw-flex tw-items-center tw-gap-[2px]">
            {label}
            {required ? <sup className={s.required}>*</sup> : null}
            {tip && <img src={qtip} alt="tip" />}
          </span>
          {children}
        </label>
      )}
    >
      <div
        className={[
          s["form-input"],
          error && s["error"],
          sizing && s[sizing],
        ].join(" ")}
        style={{ ...containerStyle }}
      >
        {sideLabel ? (
          <div className={s["side-label"]}>
            <span>{sideLabel}</span>
          </div>
        ) : null}
        <input
          style={{
            borderTopLeftRadius: inputSideBorderRadius,
            borderBottomLeftRadius: inputSideBorderRadius,
            ...textStyle,
          }}
          {...rest}
        />
        {revealSecret ? revealSecret : null}
        {error && errorText ? (
          <div className={s["error-text"]}>
            <img src={ErrorInfoIcon} alt="error" className={s["error-icon"]} />
            <span>{errorText}</span>
          </div>
        ) : null}
        {iconRight ? iconRight : null}
      </div>
    </ComponentWrapperHOC>
  );
}

interface TextAreaProps
  extends DetailedHTMLProps<
    TextareaHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
  > {
  value: string;
  label?: string;
  optional?: string;
  wrapperClassName?: string;
  wordLimit?: number;
  error?: string;
}

export function Textbox(props: TextAreaProps) {
  const {
    label,
    optional,
    wrapperClassName,
    wordLimit,
    value,
    error,
    ...rest
  } = props;
  return (
    <label
      style={{ width: "100%" }}
      className={[
        s["textarea-wrapper"],
        wrapperClassName && wrapperClassName,
      ].join(" ")}
    >
      <div className={s["textarea-content"]}>
        <span className={s["textarea-label"]}>{label}</span>
        {optional ? (
          <span className={s["textarea-optional"]}>({optional})</span>
        ) : null}
        <span className={s["textarea-limit"]}>
          {value.length}/{wordLimit}
        </span>
      </div>
      <textarea
        className={[s["textarea"], error && s["error"]].join(" ")}
        value={value}
        {...rest}
      ></textarea>
      {error && (
        <div className={s["error-text"]}>
          <img
            src={ErrorInfoIcon}
            alt="error"
            className={s["error-icon"]}
            height={12}
            width={12}
          />
          <span>{error}</span>
        </div>
      )}
    </label>
  );
}
